import { LoaderFunctionArgs, redirect } from 'react-router-dom'

import { FlowType } from '@cash-web/protos/squareup/cash/plasma/flows/FlowProto.pb'
import { getFlow } from '@cash-web/shared-data-access-api/api'
import { JourneyRoute } from '@cash-web/shared-journey-util-main'
import { prefetchCardAssets } from '@cash-web/shared-ui-card-assets/prefetchCardAssets'

import { unsupportedFlowRoutes } from './unsupportedFlowRoutes'

const loaderPrefetchMethods: Partial<Record<FlowType, () => Promise<void>>> = {
  [FlowType.CHANGE_CARD_DESIGN]: prefetchCardAssets,
  [FlowType.REQUEST_PHYSICAL_CARD]: prefetchCardAssets,
}

async function prefetchByFlowType(flowType: FlowType) {
  if (loaderPrefetchMethods[flowType]) {
    const prefetch = loaderPrefetchMethods[flowType]
    await prefetch()
  }
}

export const flowJourneyRoutes = [
  ...unsupportedFlowRoutes,
  {
    id: 'flow-type-view-money-pool',
    trackingName: 'Flow Type View Money Pool',
    path: `dl/flow/type/${FlowType.VIEW_MONEY_POOL}`,
    loader: async () => {
      throw redirect('/web/view/activity/not-supported')
    },
  },
  {
    id: 'flow-view-money-pool',
    trackingName: 'Flow View Money Pool',
    path: `dl/flow/${FlowType.VIEW_MONEY_POOL}`,
    loader: async () => {
      throw redirect('/web/view/activity/not-supported')
    },
  },
  {
    id: 'journey-get-flow-initiation-data',
    trackingName: 'Journey Get Flow Initiation Data',
    path: 'dl/flow/:initiationData',
    lazy: async () => ({
      Component: (await import('../components/JourneyGetFlow')).JourneyGetFlow,
    }),
  },
  {
    id: 'journey-get-flow-initiation-data/loader',
    trackingName: 'Journey Set Flow',
    path: 'web/flow/loader/:initiationData',
    lazy: async () => ({
      Component: (await import('../components/JourneySetFlow')).JourneySetFlow,
    }),
    loader: async ({ request, params }: LoaderFunctionArgs) => {
      const flowType = params['initiationData'] as FlowType
      void prefetchByFlowType(flowType)

      const url = new URL(request.url)
      const paramsFromSearch = url.searchParams.get('params')
      const type = url.searchParams.get('params_type')

      const initiationDataWithSearch = `${flowType}${url.search}`

      const initiationData = paramsFromSearch
        ? type
          ? `${flowType}?params_type=${type}&params=${paramsFromSearch}`
          : `${flowType}?params=${paramsFromSearch}`
        : initiationDataWithSearch

      const response = await getFlow({
        initiation_data: initiationData,
      })

      return response
    },
  },
  {
    id: 'journey-get-flow-type',
    trackingName: 'Journey Get Flow Type',
    path: 'dl/flow/type/:flowType',
    lazy: async () => ({
      Component: (await import('../components/JourneyGetFlow')).JourneyGetFlow,
    }),
  },
  {
    id: 'journey-get-flow-type/loader',
    trackingName: 'Journey Set Flow Type',
    path: 'web/flow/loader/type/:flowType',
    lazy: async () => ({
      Component: (await import('../components/JourneySetFlow')).JourneySetFlow,
    }),
    loader: async ({ params }: LoaderFunctionArgs) => {
      const response = await getFlow({
        initiation_data: params['flowType'],
      })
      return response
    },
  },
  {
    id: 'journey-complete-scenario-with-flow-token',
    trackingName: 'Journey Complete Scenario with Flow Token',
    path: 'dl/scenario/complete/:clientScenario/:flowToken',
    lazy: async () => ({
      Component: (await import('../components/JourneyCompleteScenario')).JourneyCompleteScenario,
    }),
  },
  {
    id: 'journey-complete-scenario',
    trackingName: 'Journey Complete Scenario',
    path: 'dl/scenario/:clientScenario',
    lazy: async () => ({
      Component: (await import('../components/JourneyCompleteScenario')).JourneyCompleteScenario,
    }),
  },
  {
    id: 'journey-blocker-descriptors',
    trackingName: 'Journey Blocker Descriptors',
    path: 'web/flow/blocker-descriptors',
    lazy: async () => ({
      Component: (await import('../components/JourneyBlockerDescriptors')).JourneyBlockerDescriptors,
    }),
  },
] as const satisfies JourneyRoute[]
